import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { DeleteIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Heading,
  Tr,
  useColorModeValue,
  useToast,
  useDisclosure,
  Text,
  Checkbox,
  SimpleGrid,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import Default from 'components/fields/InputField';
import { v4 as uuidv4 } from 'uuid';
import { HSeparator } from 'components/separator/Separator';
import InterfaceSelect from 'components/select';
import GlobalAlertDialog from 'components/GlobalAlertDialog';
import GlobalModal from 'components/GlobalModal';
import AssetParamDialog from '../configure/modal/assetParam';
import TableSkeleton from '../configure/components/tableSkeleton';
import { m } from 'framer-motion';
import apiUrls from '../../../api/apiUrls'; // added import
import axiosInstance from '../../../api/axiosInstance';
import { useCallback, useEffect, useState } from 'react';

const AddConfiguration = () => {
  // read device id from localStorage instead of route params
  const deviceId = localStorage.getItem('deviceId');
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'navy.700');
  const textColor = useColorModeValue('navy.700', 'white');
  const inputBg = useColorModeValue('white', 'navy.600');
  const navigate = useNavigate();
  const location = useLocation();

  const [configName, setConfigName] = useState('');
  const [configId, setConfigId] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [serialNumber, setSerialNumber] = useState('');
  const {
    isOpen: isOpenSaveConf,
    onOpen: DeviceSaveConf,
    onClose: onCloseSaveConf,
  } = useDisclosure();

  // Add new state for Create New Configuration modal
  const {
    isOpen: isOpenCreateNew,
    onOpen: onOpenCreateNew,
    onClose: onCloseCreateNew,
  } = useDisclosure();

  const [configurations, setConfigurations] = useState([
    {
      id: uuidv4(),
      name: '',
      slaveid: '',
      slaveip: '',
      interface_id: 0,
      parameters: [],
    },
  ]);
  const [originalData, setOriginalData] = useState({});
  const [currentSlaveId, setCurrentSlaveId] = useState(null);
  const [open, setOpen] = useState(false);
  const [openAssetMastermodel, setAssetMasterOpen] = useState(false);
  const [deviceType, setDeviceType] = useState(1);
  const [maxSlavePerDevice, setMaxSlavePerDevice] = useState(10);
  const [maxSlavePerInterFace, setMaxSlavePerInterFace] = useState(5);
  const [maxSlaveParameters, setMaxSlaveParameters] = useState(20);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [devicesArray, setDevicesArray] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasNext, setHasNext] = useState(true);
  const [loading, setLoading] = useState(false);

  const isSlaveChanged = (slave) => {
    const original = originalData[slave.id];
    return JSON.stringify(slave) !== JSON.stringify(original);
  };

  const writeFunctionCodes = {
    'Read Only': -1,
    'Write Coil': 5,
    '6 Label': 6,
    '16 Label': 16,
  };

  const ParamTypes = {
    '32bit float': 3,
    '32bit uint': 2,
    '32bit int': 18,
    Str: 42,
    '16bit float': 26,
    '16bit int': 14,
    '16bit uint': 1,
    Bool: 43,
  };

  const RegisterDataType = {
    DCBA: 1,
    BADC: 3,
    Bool: 2,
  };

  const Actions = {
    'Read Only': 0,
    'Read Write': 1,
  };

  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [interfaceIdMap, setInterfaceIdMap] = useState({});
  const [modbusConfigMap, setmodbusConfigMap] = useState({});
  const [interfaceNumberMap, setInterfaceNumberMap] = useState({});
  const [selectedModbusConfigId, setSelectedModbusConfigId] = useState('');

  const showError = (message) => {
    setErrorMessage(message);
    setIsErrorOpen(true);
  };

  const confirmDelete = (type, slaveId, paramId = null) => {
    setDeleteTarget({ type, slaveId, paramId });
    setIsDeleteOpen(true);
  };

  const defaultParameter = {
    id: uuidv4(),
    name: '',
    fn_code: 3,
    address: 0,
    type: 43,
    factor: 1,
    register_count: 1,
    register_data_type: 1,
    offset: 0,
    write_fn_code: -1,
    action: 0,
  };

  const handleDeleteConfirmed = () => {
    if (deleteTarget.type === 'slave') {
      setConfigurations((prev) =>
        prev.filter((slave) => slave.id !== deleteTarget.slaveId),
      );
    } else if (deleteTarget.type === 'parameter') {
      setConfigurations((prev) =>
        prev.map((slave) => {
          if (slave.id !== deleteTarget.slaveId) return slave;
          return {
            ...slave,
            parameters: slave.parameters.filter(
              (param) => param.id !== deleteTarget.paramId,
            ),
          };
        }),
      );
    }
    setIsDeleteOpen(false);
    setDeleteTarget({ type: null, slaveId: null, paramId: null });
  };

  const updateSlave = (id, field, value) => {
    // console.log(value);
    setConfigurations((prev) =>
      prev.map((slave) =>
        slave.id === id
          ? {
              ...slave,
              [field]: value,
            }
          : slave,
      ),
    );
  };

  const addParameter = (slaveId) => {
    const targetSlave = configurations.find((s) => s.id === slaveId);
    if (!targetSlave) return;

    if (targetSlave.parameters.length >= maxSlaveParameters) {
      showError(
        `You cannot add more than ${maxSlaveParameters} parameters per slave.`,
      );
      return;
    }

    const param = { ...defaultParameter, id: uuidv4() };
    setConfigurations((prev) =>
      prev.map((slave) =>
        slave.id === slaveId
          ? { ...slave, parameters: [...slave.parameters, param] }
          : slave,
      ),
    );
  };

  const updateParameter = (slaveId, paramId, field, value) => {
    setConfigurations((prev) =>
      prev.map((slave) => {
        if (slave.id !== slaveId) return slave;
        return {
          ...slave,
          parameters: slave.parameters.map((param) =>
            param.id === paramId ? { ...param, [field]: value } : param,
          ),
        };
      }),
    );
  };

  const [deleteTarget, setDeleteTarget] = useState({
    type: null,
    slaveId: null,
    paramId: null,
  });
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const handleSave = (slave) => {
    setConfigurations((prev) => {
      const index = prev.findIndex((s) => s.id === slave.id);
      const existingSlave = prev[index];

      let newVersion = 1;

      if (existingSlave) {
        const { version = 1 } = existingSlave;
        newVersion = version + 1;
      }

      const updatedSlave = {
        ...existingSlave,
        version: newVersion,
      };

      toast({
        title: 'Slave Saved.',

        status: 'success',
        duration: 2000,
        position: 'top',
        isClosable: true,
      });

      setOriginalData((prevOriginal) => ({
        ...prevOriginal,
        [slave.id]: updatedSlave,
      }));

      if (index !== -1) {
        const updated = [...prev];
        updated[index] = updatedSlave;
        return updated;
      } else {
        return [...prev, updatedSlave];
      }
    });
  };

  const handleSaveConfigurations = async () => {
    try {
      const modbusConfig = {
        addConfigToDevice: 0,
        config_name: configName,
        slaves_config: configurations.map((slave) => {
          const cleanedParameters = slave.parameters;
          return {
            interface_id: slave?.interface_id,
            id: slave?.id || '',
            version: slave?.version ? slave.version : 1,
            slave_id: slave?.slaveid || '',
            slave_ip: slave?.slaveip || '',
            // slave_port: slave?.port || '', //
            slave_name: slave?.name || '',
            parameters: cleanedParameters,
          };
        }),
      };

      if (!deviceId) {
        toast({
          title: 'No Device Selected',
          description:
            'No deviceId found in localStorage. Please ensure a device id is stored under "deviceId".',
          status: 'warning',
          duration: 3000,
          position: 'top',
          isClosable: true,
        });
        return;
      }

      // Use axiosInstance instead of fetch
      const response = await axiosInstance.post(
        apiUrls.updateDeviceModbus(deviceId),
        modbusConfig,
      );

      if (response.status === 200) {
        toast({
          title: 'Configuration Posted',
          description: `Payload for device ${deviceId} posted to server.`,
          status: 'success',
          duration: 3000,
          position: 'top',
          isClosable: true,
        });

        onCloseSaveConf();
        setConfigName('');
        // Change tab index to 1 after save
        navigate(location.pathname, { state: { activeTab: 1 } });
      } else {
        throw new Error('Unexpected response status');
      }
    } catch (err) {
      console.error('Save failed:', err);
      toast({
        title: 'Save Failed',
        description:
          err.response?.data?.error ||
          'Could not prepare or post the configurations.',
        status: 'error',
        position: 'top',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCheckboxChange = (id) => {
    setSelectedDevices((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id],
    );
  };

  const updateSlaveParams = (parameters) => {
    if (!currentSlaveId || !parameters || !Array.isArray(parameters)) {
      console.error('Invalid parameters or no slave selected');
      return;
    }

    setConfigurations((prev) =>
      prev.map((slave) => {
        if (slave.id === currentSlaveId) {
          const newParameters = parameters.map((param) => ({
            id: param.id || uuidv4(),
            name: param.name || '',
            fn_code: param.fnCode || 3,
            address: param.address || 0,
            type: ParamTypes[param.type] || 43,
            factor: param.factor || 1,
            register_count: param.regCount || 1,
            register_data_type: RegisterDataType[param.regDataType] || 1,
            offset: param.offset || 0,
            write_fn_code: writeFunctionCodes[param.writeFn] || -1,
            action: Actions[param.access] || 0,
          }));

          return {
            ...slave,
            parameters: [...slave.parameters, ...newParameters],
          };
        }
        return slave;
      }),
    );
  };

  const closeAssetParameterModal = () => {
    setAssetMasterOpen(false);
  };

  const areAllConfigurationsSaved = () => {
    return configurations.every((slave) => !isSlaveChanged(slave));
  };

  // Modified onOpenSaveConf function
  const handleSaveConfigurationClick = () => {
    if (configurations.length === 0) {
      toast({
        title: 'No Configurations',
        description:
          'Please add at least one slave configuration before saving.',
        status: 'warning',
        duration: 3000,
        position: 'top',
        isClosable: true,
      });
      return;
    }

    if (!areAllConfigurationsSaved()) {
      toast({
        title: 'Unsaved Changes',
        description:
          'Please save all slave configurations before saving the main configuration.',
        status: 'error',
        duration: 3000,
        position: 'top',
        isClosable: true,
      });
      return;
    }

    setSelectedDevices([]);
    DeviceSaveConf();
  };

  // Add function to handle create new configuration
  const handleCreateNewConfiguration = () => {
    // Reset to single default slave configuration
    const newSlave = {
      id: uuidv4(),
      name: '',
      slaveid: '',
      slaveip: '',
      interface_id: 0,
      parameters: [],
    };

    setConfigurations([newSlave]);
    setOriginalData({ [newSlave.id]: newSlave });
    setConfigName('');
    setConfigId(null);
    setSelectedDevices([]);
    setCurrentSlaveId(null);

    toast({
      title: 'New Configuration Started',
      description: 'Ready to create a new configuration.',
      status: 'info',
      duration: 2000,
      position: 'top',
      isClosable: true,
    });

    onCloseCreateNew();
  };

  // Add API calls to fetch device data
  const fetchDeviceInterfaces = useCallback(async () => {
    if (!deviceId) return;

    try {
      const keys = 'interfaces,modbus_configs';
      const resp = await axiosInstance.get(
        apiUrls.groupAttributes(deviceId, keys),
      );
      const attrMap = Object.fromEntries(
        resp.data.map((a) => [a.key, a.value]),
      );

      const modbusConfigMap =
        attrMap.modbus_configs?.reduce((acc, i) => {
          const id = i.id || '';
          const name = i.name || '';
          acc[name] = id;
          return acc;
        }, {}) || {};

      setmodbusConfigMap(modbusConfigMap);

      const interfaceIdMap =
        attrMap.interfaces?.reduce((acc, i) => {
          const id = i.interface || '';
          const name = i.name || '';
          acc[name] = id;
          return acc;
        }, {}) || {};

      const interfaceNumberMap =
        attrMap.interfaces?.reduce((acc, i) => {
          const id = i.id || '';
          const num = i.settings?.interface;
          acc[id] = num;
          return acc;
        }, {}) || {};

      setInterfaceIdMap(interfaceIdMap);
      setInterfaceNumberMap(interfaceNumberMap);
    } catch (err) {
      console.error('Failed to fetch device interfaces', err);
      toast({
        title: 'Error',
        description: 'Failed to fetch device interfaces',
        status: 'error',
        duration: 3000,
        position: 'top',
        isClosable: true,
      });
    }
  }, [deviceId, toast]);

  const fetchDeviceAttributes = useCallback(async () => {
    if (!deviceId) return;

    let keys = [
      'device_type',
      'max_modbus_slave',
      'max_slave_per_interface',
      'max_modbus_params_per_slave',
    ];
    keys = keys.join(',');

    try {
      const resp = await axiosInstance.get(
        apiUrls.toolDeviceAttributes(deviceId, keys),
      );

      const attrs = resp.data;
      const attrMap = Object.fromEntries(attrs.map((a) => [a.key, a.value]));

      setDeviceType(attrMap.device_type || 1);
      setMaxSlavePerDevice(attrMap.max_modbus_slave || 10);
      setMaxSlavePerInterFace(attrMap.max_slave_per_interface || 5);
      setMaxSlaveParameters(attrMap.max_modbus_params_per_slave || 20);
    } catch (err) {
      console.error('Error fetching device attributes', err);
      toast({
        title: 'Error',
        description: 'Failed to fetch device attributes',
        status: 'error',
        duration: 3000,
        position: 'top',
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [deviceId, toast]);

  // Add useEffect to fetch data on component mount
  useEffect(() => {
    if (deviceId) {
      setLoading(true);
      fetchDeviceInterfaces();
      fetchDeviceAttributes();
    } else {
      toast({
        title: 'No Device Selected',
        description: 'No deviceId found in localStorage.',
        status: 'warning',
        duration: 3000,
        position: 'top',
        isClosable: true,
      });
    }
  }, [deviceId, fetchDeviceInterfaces, fetchDeviceAttributes, toast]);

  const isSlaveConfigValid = (slave) => {
    if (!slave.name) return false;
    if (!slave.slaveid) return false;
    if (interfaceNumberMap[slave.interface_id] === 1 && !slave.slaveip)
      return false;
    return true;
  };

  // Helper to get interface name by interface_id
  const getInterfaceNameById = (interface_id) => {
    const iface = Object.entries(interfaceIdMap).find(
      ([, id]) => id === interface_id,
    );
    return iface ? iface[0] : '';
  };

  return (
    <>
      {loading ? (
        <TableSkeleton rowCount={5} columnCount={12} />
      ) : (
        <Box color={textColor}>
          <Flex justify="space-between" mt={1}>
            <Heading size="lg" mb={6}>
              Add Configuration
            </Heading>
            {Object.keys(modbusConfigMap).length > 0 && (
              <Box>
                {/* <Flex>
                  <Text me="3" mt={1} fontWeight="600">
                    Config:
                  </Text>
                  <InterfaceSelect
                    value={selectedModbusConfigId}
                    options={modbusConfigMap}
                    onChange={(selectedConfig) =>
                      setSelectedModbusConfigId(selectedConfig)
                    }
                  />
                </Flex> */}
              </Box>
            )}
            <Flex gap={2}>
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                border="2px"
                cursor="pointer"
                onClick={handleSaveConfigurationClick}
              >
                Save Configuration
              </Button>
              <Button
                size="sm"
                colorScheme="green"
                variant="outline"
                border="2px"
                cursor="pointer"
                onClick={onOpenCreateNew}
              >
                Add New
              </Button>
            </Flex>
          </Flex>

          {configurations.map((slave) => {
            const interfaceName = getInterfaceNameById(slave.interface_id);
            const isModbusTCP = interfaceName === 'Modbus_TCP';
            return (
              <Card key={slave.id} bg={cardBg} p={4} my={4} color={textColor}>
                <Table size="sm" mb={4}>
                  <Thead>
                    <Tr>
                      <Th borderColor="transparent">Name</Th>
                      <Th borderColor="transparent">Slave ID</Th>
                      {isModbusTCP && (
                        <>
                          <Th borderColor="transparent">Slave IP</Th>
                          {/* <Th borderColor="transparent">Slave Port</Th> */}
                        </>
                      )}
                      <Th borderColor="transparent">Interface</Th>
                      <Th borderColor="transparent" textAlign="center">
                        Actions
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td borderColor="transparent" ps={0}>
                        <Default
                          size="sm"
                          fontSize="lg"
                          placeholder="slave name"
                          value={slave.name}
                          onChange={(e) =>
                            updateSlave(slave.id, 'name', e.target.value)
                          }
                          bg={inputBg}
                          color={textColor}
                        />
                      </Td>
                      <Td borderColor="transparent" ps={0}>
                        <Default
                          size="sm"
                          fontSize="md"
                          type="number"
                          placeholder="slave id"
                          value={slave.slaveid}
                          onChange={(e) => {
                            const val = e.target.value;
                            updateSlave(
                              slave.id,
                              'slaveid',
                              val === '' ? '' : Number(val),
                            );
                          }}
                          bg={inputBg}
                          color={textColor}
                        />
                      </Td>
                      {isModbusTCP && (
                        <>
                          <Td borderColor="transparent" ps={0}>
                            <Default
                              size="sm"
                              fontSize="md"
                              placeholder="slave ip"
                              value={slave.slaveip}
                              onChange={(e) =>
                                updateSlave(slave.id, 'slaveip', e.target.value)
                              }
                              bg={inputBg}
                              color={textColor}
                            />
                          </Td>
                          {/* <Td borderColor="transparent" ps={0}>
                            <Default
                              size="sm"
                              fontSize="md"
                              placeholder="slave port"
                              value={
                                slave.port === undefined || slave.port === ''
                                  ? ''
                                  : slave.port
                              }
                              onChange={(e) => {
                                const val = e.target.value;
                                updateSlave(
                                  slave.id,
                                  'port',
                                  val === '' ? '' : Number(val),
                                );
                              }}
                              bg={inputBg}
                              color={textColor}
                            />
                          </Td> */}
                        </>
                      )}
                      <Td borderColor="transparent" ps={0}>
                        <InterfaceSelect
                          value={slave.interface_id}
                          options={interfaceIdMap}
                          onChange={(selectedInterface) =>
                            updateSlave(
                              slave.id,
                              'interface_id',
                              selectedInterface,
                            )
                          }
                        />
                      </Td>
                      <Td borderColor="transparent" textAlign="center" ps={0}>
                        <IconButton
                          aria-label="Remove slave"
                          icon={<DeleteIcon />}
                          size="sm"
                          colorScheme="red"
                          onClick={() => confirmDelete('slave', slave.id)}
                          mr={2}
                        />
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>

                <HSeparator />

                {slave.parameters.length > 0 && (
                  <Box overflowX="auto" maxWidth="100%">
                    <Table variant="simple" size="sm" mt={4}>
                      <Thead>
                        <Tr>
                          <Th borderColor="transparent">Name</Th>
                          <Th borderColor="transparent">Fn Code</Th>
                          <Th borderColor="transparent">Address</Th>
                          <Th borderColor="transparent">Type</Th>
                          <Th borderColor="transparent">Factor</Th>
                          <Th borderColor="transparent">Reg Count</Th>
                          <Th borderColor="transparent">Register Data Type</Th>
                          <Th borderColor="transparent">Offset</Th>
                          <Th borderColor="transparent">Write Fn</Th>
                          <Th borderColor="transparent">Access</Th>
                          <Th borderColor="transparent">Delete</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {slave.parameters.map((param, idx) => (
                          <Tr key={idx}>
                            <Td ps={0} borderColor="transparent">
                              <Default
                                size="sm"
                                placeholder="e.g. tmp"
                                value={param.name}
                                onChange={(e) =>
                                  updateParameter(
                                    slave.id,
                                    param.id,
                                    'name',
                                    e.target.value,
                                  )
                                }
                                bg={inputBg}
                                color={textColor}
                              />
                            </Td>
                            <Td ps={0} borderColor="transparent">
                              <Default
                                size="sm"
                                type="number"
                                value={param.fn_code}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  updateParameter(
                                    slave.id,
                                    param.id,
                                    'fn_code',
                                    val === '' ? '' : Number(val),
                                  );
                                }}
                                bg={inputBg}
                                color={textColor}
                              />
                            </Td>
                            <Td ps={0} borderColor="transparent">
                              <Default
                                size="sm"
                                type="number"
                                value={param.address}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  updateParameter(
                                    slave.id,
                                    param.id,
                                    'address',
                                    val === '' ? '' : Number(val),
                                  );
                                }}
                                bg={inputBg}
                                color={textColor}
                              />
                            </Td>
                            <Td ps={0} borderColor="transparent">
                              <InterfaceSelect
                                value={param.type}
                                options={ParamTypes}
                                onChange={(val) =>
                                  updateParameter(
                                    slave.id,
                                    param.id,
                                    'type',
                                    val,
                                  )
                                }
                              />
                            </Td>
                            <Td ps={0} borderColor="transparent">
                              <Default
                                size="sm"
                                type="number"
                                value={param.factor}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  updateParameter(
                                    slave.id,
                                    param.id,
                                    'factor',
                                    val === '' ? '' : Number(val),
                                  );
                                }}
                                bg={inputBg}
                                color={textColor}
                              />
                            </Td>
                            <Td ps={0} borderColor="transparent">
                              <Default
                                size="sm"
                                type="number"
                                value={param.register_count}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  updateParameter(
                                    slave.id,
                                    param.id,
                                    'register_count',
                                    val === '' ? '' : Number(val),
                                  );
                                }}
                                bg={inputBg}
                                color={textColor}
                              />
                            </Td>
                            <Td ps={0} borderColor="transparent">
                              <InterfaceSelect
                                value={param.register_data_type}
                                options={RegisterDataType}
                                onChange={(val) =>
                                  updateParameter(
                                    slave.id,
                                    param.id,
                                    'register_data_type',
                                    val,
                                  )
                                }
                              />
                            </Td>
                            <Td ps={0} borderColor="transparent">
                              <Default
                                size="sm"
                                type="number"
                                value={param.offset}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  updateParameter(
                                    slave.id,
                                    param.id,
                                    'offset',
                                    val === '' ? '' : Number(val),
                                  );
                                }}
                                bg={inputBg}
                                color={textColor}
                              />
                            </Td>
                            <Td ps={0} borderColor="transparent">
                              <InterfaceSelect
                                value={param.write_fn_code}
                                options={writeFunctionCodes}
                                onChange={(val) =>
                                  updateParameter(
                                    slave.id,
                                    param.id,
                                    'write_fn_code',
                                    val,
                                  )
                                }
                              />
                            </Td>
                            <Td ps={0} borderColor="transparent">
                              <InterfaceSelect
                                value={param.access || 0}
                                options={Actions}
                                onChange={(val) =>
                                  updateParameter(
                                    slave.id,
                                    param.id,
                                    'access',
                                    val,
                                  )
                                }
                              />
                            </Td>
                            <Td
                              ps={0}
                              textAlign={'end'}
                              borderColor="transparent"
                            >
                              <IconButton
                                icon={<DeleteIcon />}
                                aria-label="Delete parameter"
                                size="sm"
                                colorScheme="red"
                                onClick={() =>
                                  confirmDelete('parameter', slave.id, param.id)
                                }
                              />
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}

                <AssetParamDialog
                  open={openAssetMastermodel}
                  onClose={closeAssetParameterModal}
                  updateSlaveParams={updateSlaveParams}
                />

                <Flex justify="center" mt={3}>
                  <Button
                    size="sm"
                    colorScheme="green"
                    variant="outline"
                    border="2px"
                    cursor="pointer"
                    onClick={() => handleSave(slave)}
                    isDisabled={!isSlaveChanged(slave)}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    mx="2"
                    variant="outline"
                    border="2px"
                    colorScheme="orange"
                    cursor="pointer"
                    onClick={() => addParameter(slave.id)}
                  >
                    Add Parameter
                  </Button>
                  <Button
                    variant="outline"
                    border="2px"
                    colorScheme="orange"
                    size="sm"
                    cursor="pointer"
                    onClick={() => {
                      setCurrentSlaveId(slave.id);
                      setOpen(true);
                    }}
                  >
                    Add Asset
                  </Button>
                </Flex>
              </Card>
            );
          })}

          <AssetParamDialog
            open={open}
            onClose={() => {
              setOpen(false);
              setCurrentSlaveId(null);
            }}
            updateSlaveParams={updateSlaveParams}
          />

          <GlobalAlertDialog
            isOpen={isDeleteOpen}
            onClose={() => setIsDeleteOpen(false)}
            onConfirm={handleDeleteConfirmed}
            title={
              deleteTarget.type === 'slave'
                ? 'Delete Slave'
                : 'Delete Parameter'
            }
            body={
              <>
                Are you sure you want to delete this{' '}
                {deleteTarget.type === 'slave' ? 'slave' : 'parameter'}? This
                action cannot be undone.
              </>
            }
            confirmText="Delete"
            cancelText="Cancel"
            showCancel={true}
          />

          <GlobalAlertDialog
            isOpen={isErrorOpen}
            onClose={() => setIsErrorOpen(false)}
            onConfirm={() => setIsErrorOpen(false)}
            title="Validation Error"
            body={errorMessage}
            confirmText="OK"
            showCancel={false}
          />

          {/* Modified Save Configuration Modal */}
          <GlobalModal
            isOpen={isOpenSaveConf}
            onClose={() => {
              onCloseSaveConf();
              setConfigName('');
            }}
            title="Save Configuration"
            size="md"
            bg={cardBg}
          >
            <Default
              fontSize="lg"
              border="2px"
              mb="4"
              placeholder="Enter Configuration Name"
              value={configName}
              onChange={(e) => setConfigName(e.target.value)}
              bg={inputBg}
              color={textColor}
            />
            {!configName && (
              <Text fontSize="sm" color="red.500" mb={2}>
                Configuration name is required.
              </Text>
            )}

            <Flex justify="center" mt={4} gap={3}>
              <Button
                size="sm"
                colorScheme="green"
                variant="outline"
                border="2px"
                isDisabled={!configName}
                onClick={handleSaveConfigurations}
                cursor="pointer"
              >
                Save Configuration
              </Button>
              <Button
                size="sm"
                colorScheme="gray"
                variant="outline"
                border="2px"
                cursor="pointer"
                onClick={() => {
                  onCloseSaveConf();
                  setConfigName('');
                }}
              >
                Cancel
              </Button>
            </Flex>
          </GlobalModal>

          {/* Create New Configuration Confirmation Modal */}
          <GlobalModal
            isOpen={isOpenCreateNew}
            onClose={onCloseCreateNew}
            title="Create New Configuration"
            size="md"
            bg={cardBg}
          >
            <Text mb={4}>
              Are you sure you want to create a new configuration? This will
              clear all current configurations.
            </Text>
            <Flex justify="center" gap={3}>
              <Button
                size="sm"
                colorScheme="green"
                variant="outline"
                border="2px"
                cursor="pointer"
                onClick={handleCreateNewConfiguration}
              >
                Yes, Create New
              </Button>
              <Button
                size="sm"
                colorScheme="gray"
                variant="outline"
                border="2px"
                cursor="pointer"
                onClick={onCloseCreateNew}
              >
                Cancel
              </Button>
            </Flex>
          </GlobalModal>
        </Box>
      )}
    </>
  );
};

export default AddConfiguration;
